import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as AppState from '../AppState';
import * as React from 'react';
import './MapDrawing.scss';

const useMapDrawing = (gmap: google.maps.Map | undefined, isMobile: boolean): [() => JSX.Element, (map: google.maps.Map) => void] => {


	const [polygonState, setPolygonState] = useState<google.maps.Polygon | null>(null);
	const [isDrawing, setIsDrawing] = useState(false);

	const setGsMapOptions = useSetRecoilState(AppState.gsMapOptions);
	const gsMapScriptLoaded = useRecoilValue(AppState.gsMapScriptLoaded);
	const [gsFilters, setGsFilters] = useRecoilState(AppState.gsFilters);

	const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);

	const polygonStyles = {
		fillColor: '#f47c28',
		fillOpacity: .5,
		strokeColor: '#00828f',
		strokeWeight: 3,
		clickable: false,
		editable: false,
		zIndex: 1,
	};

	useEffect(() => {
		if (gsMapScriptLoaded && gsFilters.searchArea !== undefined && polygonState === null) {

			const convertSearchAreaToPolygonPath = (searchArea: any) => {
				const paths = searchArea.map((LatLng: any) => ({ lat: LatLng[1], lng: LatLng[0] }));
				return paths;
			};

			const polygon: google.maps.Polygon = new google.maps.Polygon({
				...polygonStyles,
				paths: convertSearchAreaToPolygonPath(gsFilters.searchArea)
			});

			if (gmap)
				polygon.setMap(gmap);

			setPolygonState(polygon);
		}

	}, [JSON.stringify(gsFilters.searchArea)]);

	const endDrawing = (map: google.maps.Map | undefined) => {
		setIsDrawing(false);
		map?.setOptions({
			gestureHandling: 'greedy'
		});
		setGsMapOptions((currVal) => ({ ...currVal, gestureHandling: 'greedy' }));
		drawingManagerRef.current?.setDrawingMode(null);
	};

	const initializeMapDrawingManager = (map: google.maps.Map) => {

		const drawingManager = new google.maps.drawing.DrawingManager({
			drawingMode: null,
			drawingControl: false,
			polygonOptions: polygonStyles,
			map: map
		});

		drawingManagerRef.current = drawingManager;

		google.maps.event.addListener(drawingManager, 'polygoncomplete', function (polygon: any) {
			setPolygonState(polygon);

			const latLngArray = polygon.getPath().getArray();

			const roundToNPoints = (number: number, points: number) => {
				const modifier = Math.pow(10.0, points);
				return Math.round(number * modifier) / modifier;
			};

			const latLngs = latLngArray.map((latLng: any) => ([roundToNPoints(latLng.lng(), 4), roundToNPoints(latLng.lat(), 4)]));

			setGsFilters(currVal => ({ ...currVal, searchArea: latLngs }));

			endDrawing(map);
		});
	};

	const MapDrawingInfoBanner = () => {

		const handleCancelDrawing = () => {
			endDrawing(gmap);
		};

		const desktopContent = {
			h4: <h4>Please <strong>create a boundary</strong> by placing points around where you would like to see homes,</h4>,
			div: <div>to finish, double click, or click on your starting point.</div>
		};
		const mobileContent = {
			h4: <h4>Please <strong>create a boundary</strong> by placing points</h4>
		};

		return <div className={'d-flex justify-content-between map-drawing-info-banner'}>
			<div className='d-flex flex-column'>
				{!isMobile ? desktopContent.h4 : mobileContent.h4}
				{!isMobile && desktopContent.div}
			</div>
			<button onClick={handleCancelDrawing} className='cc-btn--outline cc-btn cc-btn--negative-outline-hs cc-btn--reset-text '>Cancel</button>
		</div>;
	};

	const MapDrawingControls = () => {
		const startDrawing: MouseEventHandler<HTMLButtonElement> = () => {
			setIsDrawing(true);
			gmap?.setOptions({
				gestureHandling: 'none'
			});
			setGsMapOptions((currVal) => ({ ...currVal, gestureHandling: 'none' }));
			drawingManagerRef.current?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);

		};

		const clearDrawing: MouseEventHandler<HTMLButtonElement> = () => {
			setGsFilters(currVal => ({ ...currVal, searchArea: undefined }));
			polygonState?.setMap(null);
			setPolygonState(null);
		};

		return <>
			<div className="map-drawing-control-container" >
				{isDrawing && <MapDrawingInfoBanner />}
				{!isDrawing && (polygonState === null ?
					<button
						className={'cc-btn--outline cc-btn cc-btn--teal-outline-hs cc-btn--reset-text map-drawing-control-button'}
						onClick={startDrawing}
					>
						Draw
						<i className="fa-map-marker-alt fad text-secondary ms-2" />
					</button>
					:
					<button
						className='cc-btn--outline cc-btn cc-btn--teal-outline-hs cc-btn--reset-text map-drawing-control-button'
						onClick={clearDrawing}
					>
						Clear Boundary
						<i className="fa-times far text-secondary ms-2 mt-1" />
					</button>)
				}
			</div>
		</>;
	};

	return [MapDrawingControls, initializeMapDrawingManager];
};
export default useMapDrawing;
